// Fulltext search API integration

import { clearPagination } from './helpers.js';
import { getTranslation, showSearchError } from './helpers.js';
import { getCachedResults, cacheResults } from './cache.js';
import { 
    getIsFulltextSearchInProgress, 
    setIsFulltextSearchInProgress,
    getHasFulltextSearchBeenRun,
    setHasFulltextSearchBeenRun,
    getLastFulltextSearchState,
    setLastFulltextSearchState
} from './state.js';
import { renderFulltextResults } from './render-results.js';

// Get debug mode from URL
const urlParams = new URLSearchParams(window.location.search);
const debugMode = urlParams.get('debug');

/**
 * Perform fulltext search with caching and error handling
 * @param {string} query - Search query
 * @param {number} page - Page number (default: 1)
 */
export async function performFulltextSearch(query, page = 1) {
    // Check if user is authenticated - block fulltext search for guests
    const isAuthenticated = !!(window.SERVER_AUTH_STATUS && window.SERVER_AUTH_STATUS.isAuthenticated);
    if (!isAuthenticated) {
        if (window.HyperLog) {
            window.HyperLog.debug('[performFulltextSearch] Guest attempted fulltext search, redirecting to login', { query, page });
        }
        // Redirect to login with proper next parameter
        const currentUrl = window.location.pathname + (window.location.search || '') + '#fulltext';
        window.location.href = `/login?next=${encodeURIComponent(currentUrl)}`;
        return;
    }
    
    // Save restoration flag at the start of function to use it later when updating URL
    // This prevents race conditions where flag might be reset before URL update happens
    const isRestoringState = window.__isRestoringBrowserState === true;
    
    // Flush debounce to ensure latest filter changes (categories, dates) are applied before search
    // This ensures that when user presses Enter or clicks search button, pending filter changes are immediately applied
    if (window.filterManager && typeof window.filterManager.flushDebounce === 'function') {
        window.filterManager.flushDebounce();
    }
    
    // Flush URL debounce to ensure URL is updated with latest filters before search
    if (window.filterController && typeof window.filterController.flushUrlDebounce === 'function') {
        window.filterController.flushUrlDebounce();
    }
    
    // Идемпотентность: если поиск уже выполняется, игнорируем повторный вызов
    // Но разрешаем принудительный сброс флага через window.__forceResetSearchFlag
    if (getIsFulltextSearchInProgress() && !window.__forceResetSearchFlag) {
        if (window.HyperLog) {
            window.HyperLog.debug('[performFulltextSearch] Search already in progress, skipping duplicate call', { query, page });
        }
        return;
    }
    
    // Reset force flag if it was set
    if (window.__forceResetSearchFlag) {
        window.__forceResetSearchFlag = false;
        if (window.HyperLog) {
            window.HyperLog.debug('[performFulltextSearch] Force reset flag detected, allowing search', { query, page });
        }
    }
    
    // Validate query - don't show spinner or proceed if query is empty
    const trimmedQuery = (query || '').toString().trim();
    if (!trimmedQuery) {
        if (window.HyperLog) {
            window.HyperLog.debug('[performFulltextSearch] Empty query, skipping search', { query, page });
        }
        // Show error message for empty query
        const $container = $('#nav-full-text');
        const $empty = $container.find('.empty-content');
        if ($empty.length) {
            const errorText = getTranslation('search_empty_query_error', 'Please enter a search query');
            $empty.removeClass('d-none').find('.mb-1').text(errorText);
        }
        // Ensure Content tab is active to show the error
        try {
            const btn = document.getElementById('nav-full-text-card-tab');
            if (btn && window.bootstrap && typeof window.bootstrap.Tab === 'function') { 
                new window.bootstrap.Tab(btn).show(); 
            }
        } catch(_) { /* noop */ }
        return;
    }
    
    // Устанавливаем флаг выполнения поиска
    setIsFulltextSearchInProgress(true);
    setHasFulltextSearchBeenRun(true);
    
    // Clear pagination only if query changed (new search), not on pagination (same query, different page)
    const lastState = getLastFulltextSearchState();
    const isNewQuery = !lastState || lastState.query !== trimmedQuery;
    if (isNewQuery) {
        clearPagination(true);
    }
    
    try {
        window.__forceContentTab = true;
        
        // Show spinner function - only if no results exist yet
        const showSpinner = () => {
            // Get container AFTER tab activation (so it's in DOM and visible)
            const $container = $('#nav-full-text');
            if (!$container.length) {
                console.warn('[Fulltext Search] Container #nav-full-text not found');
                return;
            }
            
            // Check if there are already results - spinner should only show if container is empty
            const $resultsContainer = $container.find('#fulltext-results-container');
            const hasResults = $resultsContainer.length > 0 && $resultsContainer.find('.search-result-item').length > 0;
            
            // Only show spinner if there are no results yet
            if (hasResults) {
                return; // Don't show spinner if results already exist
            }
            
            let $initialSpinner = $container.find('.fulltext-initial-spinner');
            if ($initialSpinner.length) {
                // Remove d-none class to show spinner
                $initialSpinner.removeClass('d-none');
                // Force display to ensure visibility
                $initialSpinner[0].style.display = 'flex';
            } else {
                // Create spinner if it doesn't exist
                $initialSpinner = $('<div class="fulltext-initial-spinner"><div class="spinner-ultra" aria-label="Loading" title="Loading"></div></div>');
                $container.append($initialSpinner);
            }
        };
        
        // Activate tab first, then show spinner after tab is visible
        try {
            const btn = document.getElementById('nav-full-text-card-tab');
            if (btn && window.bootstrap && typeof window.bootstrap.Tab === 'function') {
                const tabInstance = new window.bootstrap.Tab(btn);
                tabInstance.show();
                // Wait for tab to be shown (Bootstrap fires 'shown.bs.tab' event)
                $(btn).one('shown.bs.tab', () => {
                    // Show spinner after tab is fully visible
                    setTimeout(showSpinner, 100);
                });
                // Fallback: show spinner after delay if event doesn't fire
                setTimeout(showSpinner, 300);
            } else {
                // No Bootstrap, try to show spinner immediately
                setTimeout(showSpinner, 100);
            }
        } catch(err) {
            console.error('[Fulltext Search] Error activating tab:', err);
            // Fallback: show spinner after delay
            setTimeout(showSpinner, 200);
        }
        
        const $container = $('#nav-full-text');
        const $empty = $container.find('.empty-content');
        
        // Don't hide initial spinner here - it will be hidden when results are rendered
        // Hide old spinner if it exists (legacy)
        const $spinner = $container.find('.fulltext-loading-spinner');
        if ($spinner.length) {
            $spinner.addClass('d-none').hide();
        }
        $empty.addClass('d-none');
        
        // Update request text with current query (safe injection)
        try {
            const $req = $container.find('.search-request-text');
            if ($req.length) {
                const labelText = ($req.find('b').first().text() || 'Request:');
                $req.find('b').first().text(labelText);
                const $text = $req.find('.fulltext-request-text');
                if ($text.length) { 
                    $text.text(trimmedQuery); 
                } else { 
                    $req.append(' ').append($('<span class="fulltext-request-text">').text(trimmedQuery)); 
                }
                // Reset change markers
                const badge = $('#nav-full-text-card-tab .fulltext-request-refresh-badge'); 
                if (badge.length) badge.addClass('d-none');
                const tx = $req.find('.fulltext-request-text'); 
                if (tx.length) tx.removeClass('fulltext-request-changed');
                
                // Hide search time timer when starting new search
                const $right = $req.find('.search-request-text__right');
                if ($right.length) {
                    $right.empty();
                }
                
                // Show Loading... indicator
                const $content = $req.find('.search-request-text__content');
                if ($content.length) {
                    let $loading = $content.find('.search-request-text__loading');
                    if (!$loading.length) {
                        // Get translation for Loading...
                        const loadingText = getTranslation('ui_loading', 'Loading...');
                        $loading = $('<span class="search-request-text__loading">' + loadingText + '</span>');
                        $content.append($loading);
                    }
                    $loading.removeClass('d-none').show();
                }
            }
        } catch(__) { /* noop */ }
        
        $container.addClass('loading');
        window.QUERY = trimmedQuery;
        
        // Extract filters from URL (cat, dateFrom, dateTo)
        const urlParams = new URLSearchParams(window.location.search);
        const cat = urlParams.get('cat');
        const dateFrom = urlParams.get('dateFrom');
        const dateTo = urlParams.get('dateTo');
        
        // Build search params with filters
        const searchParams = { sq: trimmedQuery, page };
        if (cat) searchParams.cat = cat;
        if (dateFrom) searchParams.dateFrom = dateFrom;
        if (dateTo) searchParams.dateTo = dateTo;
        
        // Check cache first
        const filters = { cat: cat || '', dateFrom: dateFrom || '', dateTo: dateTo || '' };
        const cached = getCachedResults(trimmedQuery, page, filters);
        if (cached) {
            if (debugMode === '1') {
                console.log('[SEARCH CACHE] Cache hit for:', trimmedQuery, 'page:', page);
            }
            // Render results first, then scroll (if needed) after rendering completes
            renderFulltextResults(cached);
            // Save current search state
            try {
                setLastFulltextSearchState({ query: trimmedQuery, ...filters });
            } catch(__) {}
            // Skip scroll in render-results.js for cache hits (we handle it here)
            window.__skipScrollAfterRender = true;
            // Scroll after rendering (only for new queries, not pagination)
            if (isNewQuery) {
                setTimeout(() => {
                    const containerEl = document.getElementById('nav-full-text');
                    if (containerEl) {
                        // Scroll only upward: check if we're below the container
                        const rect = containerEl.getBoundingClientRect();
                        const currentScrollY = window.scrollY || window.pageYOffset || 0;
                        const containerTop = rect.top + currentScrollY;
                        
                        // Scroll only if current position is below container (scroll upward)
                        if (currentScrollY > containerTop) {
                            containerEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                    }
                }, 200); // Longer delay to ensure rendering is complete
            }
            return;
        }
        
        const resp = await window.searchApi.search(searchParams);
        
        // Cache successful results
        if (resp && resp.success !== false && resp.results) {
            cacheResults(trimmedQuery, page, filters, resp);
        }
        
        // Check if API returned an error response
        if (resp && resp.success === false && resp.error) {
            const errorMessage = resp.error.toLowerCase();
            const isEmptyQueryError = errorMessage.includes('empty') || errorMessage.includes('query');
            if (isEmptyQueryError) {
                const errorText = getTranslation('search_empty_query_error', 'Please enter a search query');
                showSearchError(errorText, true);
                return;
            }
        }
        
        // Store pagination info from API response for use in renderFiles
        if (resp && resp.pagination) {
            if (resp.pagination.start_index !== undefined) {
                window.currentPaginationStartIndex = resp.pagination.start_index;
            }
            if (resp.pagination.items_per_page !== undefined) {
                window.currentPaginationItemsPerPage = resp.pagination.items_per_page;
            }
        } else {
            // Clear if not present
            window.currentPaginationStartIndex = undefined;
            window.currentPaginationItemsPerPage = undefined;
        }
        
        if (debugMode === '1') {
            console.log('[PERFORM SEARCH DEBUG] Calling renderFulltextResults from performFulltextSearch');
            console.log('[PERFORM SEARCH DEBUG] Response pagination:', resp?.pagination);
        }
        // Mark that we should scroll after render (for API results, not cache)
        // But skip scroll if we're appending results (infinite scroll mode)
        if (!window.__isAppendingResults) {
            window.__skipScrollAfterRender = false;
        }
        renderFulltextResults(resp);
        
        // Save current search state (query + filters) for mismatch detection
        try {
            setLastFulltextSearchState({
                query: trimmedQuery,
                cat: cat || '',
                dateFrom: dateFrom || '',
                dateTo: dateTo || ''
            });
        } catch(__) { /* noop */ }
        
        // Preserve state in URL (use pushState for significant changes like search execution)
        // Skip URL update if we're restoring state from browser history
        // Note: URL is updated by filterController.updateUrlWithFilters with immediate flag
        // This ensures that any pending debounced updates are flushed first
        // Use saved flag from function start to prevent race conditions
        try {
            if (!isRestoringState) {
                const debugMode = new URLSearchParams(window.location.search).get('debug') === '1';
                
                // Check if URL already matches the query (excluding hash)
                // If URL already matches, don't call updateUrlWithFilters to avoid duplicate history entries
                const currentUrl = new URL(window.location.href);
                const currentQuery = currentUrl.searchParams.get('sq') || '';
                const urlAlreadyMatches = currentQuery.trim() === trimmedQuery.trim();
                
                if (debugMode) {
                    console.log('[performFulltextSearch] 🔍 URL check:', {
                        query: trimmedQuery,
                        page,
                        currentUrl: window.location.href,
                        currentQuery,
                        urlAlreadyMatches,
                        willCallUpdateUrl: !urlAlreadyMatches
                    });
                }
                
                // Update URL with query and hash through debounced mechanism
                // Hash will be updated through updateUrlWithFilters with 2-second debounce
                if (!urlAlreadyMatches) {
                    // Update filter controller first to flush any pending debounced updates
                    if (window.filterController) {
                        window.filterController.activeFilters.searchQuery = trimmedQuery;
                        if (typeof window.filterController.updateUrlWithFilters === 'function') {
                            if (debugMode) {
                                console.log('[performFulltextSearch] 🔄 Calling updateUrlWithFilters with #fulltext hash (URL does not match query)');
                            }
                            // This will flush pending updates and create a new entry with current state
                            // Hash #fulltext will be updated through debounce mechanism (2 seconds)
                            window.filterController.updateUrlWithFilters(true, '#fulltext'); // true = immediate, '#fulltext' = desired hash
                        }
                    }
                } else {
                    // URL already matches query, but hash might need updating
                    // Update hash through debounced mechanism if needed
                    if (window.filterController && typeof window.filterController.updateUrlWithFilters === 'function') {
                        const currentHash = window.location.hash;
                        if (currentHash !== '#fulltext') {
                            if (debugMode) {
                                console.log('[performFulltextSearch] 🔄 Calling updateUrlWithFilters to update hash to #fulltext (URL matches query)');
                            }
                            // Update hash through debounce mechanism
                            window.filterController.updateUrlWithFilters(true, '#fulltext'); // true = immediate, '#fulltext' = desired hash
                        } else if (debugMode) {
                            console.log('[performFulltextSearch] ✅ Hash already #fulltext, skipping update');
                        }
                    }
                }
            } else if (debugMode) {
                console.log('[performFulltextSearch] ⏭️ Skipping URL update - restoring from browser history');
            }
        } catch(__) { /* noop */ }
    } catch (error) {
        try {
            const errorMessage = error && error.message ? error.message : '';
            const isEmptyQueryError = errorMessage.toLowerCase().includes('empty') || errorMessage.toLowerCase().includes('query');
            const errorText = isEmptyQueryError 
                ? getTranslation('search_empty_query_error', 'Please enter a search query')
                : getTranslation('search_error', 'Search error');
            showSearchError(errorText, isEmptyQueryError);
        } catch(__) {}
    } finally {
        // Сбрасываем флаг выполнения поиска
        setIsFulltextSearchInProgress(false);
        
        try {
            const $container = $('#nav-full-text');
            $container.removeClass('loading');
            // Ensure spinner is hidden
            const $spinner = $container.find('.fulltext-loading-spinner');
            if ($spinner.length) {
                $spinner.addClass('d-none').hide();
            }
        } catch(__) {}
        // Allow file tab auto-switches again after initial content load
        setTimeout(() => { try { window.__forceContentTab = false; } catch(_) {} }, 0);
    }
}

